import { useRef, useCallback, createRef } from "react";
import { Images } from "lucide-react";
import { domToPng } from "modern-screenshot";
import type { StorySlide } from "../data/types";

// Cache embedded font CSS so we only fetch once
let fontCSSCache: string | null = null;
async function getEmbeddedFontCSS(): Promise<string> {
  if (fontCSSCache) return fontCSSCache;
  // Fetch Google Fonts CSS with woff2 format (Chrome UA gets woff2 URLs)
  const cssUrl = "https://fonts.googleapis.com/css2?family=Lora:wght@500;600;700&family=Inter:wght@400;500;600;700&family=Amiri:wght@400;700&display=swap";
  const cssRes = await fetch(cssUrl);
  let css = await cssRes.text();
  // Find all font file URLs and replace with base64 data URIs
  const urlMatches = [...css.matchAll(/url\((https:\/\/fonts\.gstatic\.com\/[^)]+)\)/g)];
  for (const match of urlMatches) {
    const fontUrl = match[1];
    const fontRes = await fetch(fontUrl);
    const buf = await fontRes.arrayBuffer();
    const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
    const mime = fontUrl.includes(".woff2") ? "font/woff2" : "font/woff";
    css = css.replace(fontUrl, `data:${mime};base64,${b64}`);
  }
  fontCSSCache = css;
  return css;
}
import { Carousel } from "./Carousel";

function renderHighlighted(text: string) {
  return text.split(/(\*\*.*?\*\*|\n\n|\n)/).map((part, k) =>
    part === "\n\n" ? (
      <span key={k} style={{ display: "block", height: 24 }} />
    ) : part === "\n" ? (
      <br key={k} />
    ) : part.startsWith("**") && part.endsWith("**") ? (
      <mark key={k} style={{ backgroundColor: "#fbbf24", padding: "1px 4px", borderRadius: 3, color: "#021a19", fontWeight: 700 }}>
        {part.slice(2, -2)}
      </mark>
    ) : (
      <span key={k}>{part}</span>
    )
  );
}

function SlideTitle({ children, as: Tag = "h2" }: { children: React.ReactNode; as?: "h1" | "h2" }) {
  return (
    <Tag style={{ fontFamily: "var(--font-heading)", fontSize: Tag === "h1" ? 36 : 32, fontWeight: 700, color: "white", margin: Tag === "h1" ? "0 0 40px" : "0 0 36px", lineHeight: 1.3 }}>
      {children}
    </Tag>
  );
}

function SlideBody({ children, italic }: { children: React.ReactNode; italic?: boolean }) {
  return (
    <p style={{ fontFamily: "var(--font-body)", fontSize: 20, color: "rgba(255,255,255,0.85)", margin: 0, lineHeight: 1.6, ...(italic && { fontStyle: "italic" as const }) }}>
      {children}
    </p>
  );
}

export function StoryPlayer({
  slides: story,
  emoji,
  capturePrefix,
  bgColor = "#1e3a5f",
  showSwipeHint = false,
}: {
  slides: StorySlide[];
  emoji?: string;
  capturePrefix?: string;
  bgColor?: string;
  showSwipeHint?: boolean;
}) {
  const storyRefs = useRef<React.RefObject<HTMLDivElement | null>[]>([]);

  const captureAll = useCallback(async () => {
    const prefix = capturePrefix || "story";
    const fontCSS = await getEmbeddedFontCSS();
    const files: File[] = [];
    for (let i = 0; i < storyRefs.current.length; i++) {
      const ref = storyRefs.current[i];
      if (!ref.current) continue;
      const el = ref.current;
      const dataUrl = await domToPng(el, { scale: 3, font: { cssText: fontCSS } });
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      files.push(new File([blob], `${prefix}-slide-${i + 1}.png`, { type: "image/png" }));
    }
    if (navigator.share && navigator.canShare?.({ files })) {
      try { await navigator.share({ files }); } catch {}
    } else {
      for (const file of files) {
        const link = document.createElement("a");
        link.download = file.name;
        link.href = URL.createObjectURL(file);
        link.click();
        URL.revokeObjectURL(link.href);
      }
    }
  }, [capturePrefix]);

  const renderSlide = (slide: StorySlide, i: number, ref: React.RefObject<HTMLDivElement | null>) => (
    <div ref={ref} key={i} style={{ background: bgColor, padding: "80px 32px", display: "flex", flexDirection: "column", justifyContent: "center", width: "100%", aspectRatio: "9/16", boxSizing: "border-box", overflow: "hidden", position: "relative" }}>
      {showSwipeHint && (
        <span style={{ position: "absolute", top: 24, left: 0, right: 0, textAlign: "center", fontFamily: "var(--font-body)", fontSize: 13, color: "rgba(255,255,255,0.7)" }}>
          {i + 1}/{story.length}
        </span>
      )}
      {slide.type === "hook" && (
        <div style={{ textAlign: "center" }}>
          {emoji && <div style={{ fontSize: 48, lineHeight: 1, marginBottom: 16 }}>{emoji}</div>}
          <SlideTitle as="h1">{renderHighlighted(slide.title || "")}</SlideTitle>
          {slide.body && <SlideBody>{renderHighlighted(slide.body)}</SlideBody>}
          {showSwipeHint && (
            <p style={{ fontFamily: "var(--font-body)", fontSize: 13, color: "rgba(255,255,255,0.6)", margin: "32px 0 0", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              Geser untuk lanjut <span style={{ display: "inline-block", animation: "nudge 1.5s ease-in-out infinite" }}>→</span>
            </p>
          )}
        </div>
      )}

      {slide.type === "convo" && (
        <div>
          {slide.title && (
            <p style={{ fontFamily: "var(--font-body)", fontSize: 16, color: "rgba(255,255,255,0.5)", margin: "0 0 16px", textAlign: "center", fontWeight: 600 }}>
              {slide.title}
            </p>
          )}
          {slide.claim && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ backgroundColor: "rgba(255,255,255,0.14)", borderRadius: 14, padding: "20px 22px" }}>
                <span style={{ fontFamily: "Georgia, serif", fontSize: 48, color: "rgba(255,255,255,0.15)", lineHeight: 0.5, display: "block", marginBottom: 10 }}>"</span>
                <p style={{ fontFamily: "var(--font-body)", fontSize: 20, color: "white", margin: 0, lineHeight: 1.7, fontStyle: "italic" }}>
                  {renderHighlighted(slide.claim)}
                </p>
              </div>
              {slide.response && (
                <div style={{ padding: "0 4px" }}>
                  <p style={{ fontFamily: "var(--font-heading)", fontSize: 18, color: "#38bdf8", margin: "0 0 8px", fontWeight: 700 }}>
                    Padahal...
                  </p>
                  <SlideBody>{renderHighlighted(slide.response)}</SlideBody>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {slide.type === "source" && (
        <div style={{ textAlign: "center" }}>
          {slide.arabic && (
            <p style={{ fontFamily: "var(--font-arabic), serif", fontSize: 32, color: "white", margin: "0 0 24px", lineHeight: 2, direction: "rtl" }}>
              {slide.arabic}
            </p>
          )}
          {slide.arabic && <div style={{ width: 60, height: 1, backgroundColor: "rgba(255,255,255,0.3)", margin: "0 auto 24px" }} />}
          {slide.body && <SlideBody italic>{renderHighlighted(slide.body)}</SlideBody>}
          {slide.title && (
            <p style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "rgba(255,255,255,0.5)", margin: "20px 0 0", fontWeight: 600 }}>
              {slide.title}
            </p>
          )}
        </div>
      )}

      {slide.type === "reveal" && (
        <div style={{ textAlign: "center" }}>
          {slide.title && <SlideTitle>{renderHighlighted(slide.title.replace(/\p{Emoji_Presentation}/gu, "").trim())}</SlideTitle>}
          {slide.body && <SlideBody>{renderHighlighted(slide.body)}</SlideBody>}
        </div>
      )}

      {(slide.type === "insight" || slide.type === "engagement") && (
        <div style={{ textAlign: "center" }}>
          {slide.title && <SlideTitle>{slide.title}</SlideTitle>}
          {slide.body && <SlideBody>{renderHighlighted(slide.body)}</SlideBody>}
        </div>
      )}

    </div>
  );

  if (storyRefs.current.length !== story.length) {
    storyRefs.current = story.map(() => createRef<HTMLDivElement>());
  }

  return (
    <div>
      <Carousel>
        {story.map((slide, i) => renderSlide(slide, i, storyRefs.current[i]))}
      </Carousel>
      <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
        <button
          onClick={captureAll}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            border: "1px solid rgba(255,255,255,0.2)",
            backgroundColor: "rgba(255,255,255,0.1)",
            borderRadius: 20, padding: "6px 14px",
            cursor: "pointer", color: "rgba(255,255,255,0.6)",
            fontFamily: "var(--font-body)", fontSize: 13,
          }}
        >
          <Images size={14} /> Simpan semua slide
        </button>
      </div>
    </div>
  );
}
